import React from 'react';
import { useInRouterContext, BrowserRouter, HashRouter } from 'react-router-dom';

/**
 * Router type preference for RouterChecker.
 * - 'browser': Use BrowserRouter (history API, requires server config)
 * - 'hash': Use HashRouter (hash-based routing, works everywhere)
 * - 'none': Don't create router, expect parent to provide one
 */
export type RouterType = 'browser' | 'hash' | 'none';

export interface RouterCheckerProps {
    children: React.ReactNode;
    /**
     * Router preference when no parent router exists:
     * - 'browser': Use BrowserRouter (default for standalone apps)
     * - 'hash': Use HashRouter (default for embeds/web components)
     * - 'none': Don't create router - will error if no parent router exists
     */
    routerType?: RouterType;
    /** Base URL path for routes (e.g., '/map' or '/') */
    basename?: string;
}

/**
 * RouterChecker conditionally wraps children with a Router only if
 * a router context doesn't already exist in the component tree.
 *
 * This enables the map component to:
 * - Share the parent app's router when used as a React component (inline mode)
 * - Create its own router when used as Web Component or JS API (isolated mode)
 *
 * @example
 * ```tsx
 * // In Layout.tsx - will use parent router if exists, otherwise create one
 * <RouterChecker routerType="hash" basename="/">
 *   <Routes>
 *     <Route path="/event/:slug" element={<EventPage />} />
 *   </Routes>
 * </RouterChecker>
 * ```
 *
 * @example
 * ```tsx
 * // Inline mode - parent has router, RouterChecker just passes through
 * <BrowserRouter> // Parent app
 *   <BalloonEventsMap /> // Uses RouterChecker internally
 * </BrowserRouter>
 * // Result: Map uses parent's BrowserRouter, routes are /event/slug
 * ```
 *
 * @example
 * ```tsx
 * // Isolated mode - no parent router, RouterChecker creates HashRouter
 * <balloon-events-map /> // Web component
 * // Result: Map creates HashRouter, routes are /#/event/slug
 * ```
 */
export const RouterChecker: React.FC<RouterCheckerProps> = ({
    children,
    routerType = 'browser',
    basename = '/',
}) => {
    // Check if we're already inside a Router by using react-router's hook
    const isInRouter = useInRouterContext();

    // If we're already in a router context, just render children
    if (isInRouter) {
        if (import.meta.env?.DEV) {
            console.log('[RouterChecker] Using existing parent router context');
        }
        return <>{children}</>;
    }

    // No parent router - handle based on routerType preference
    if (routerType === 'none') {
        // 'none' means we expected a parent router but there isn't one
        // This shouldn't happen in normal usage, but let's handle gracefully
        console.warn(
            '[RouterChecker] routerType="none" but no parent router found. ' +
            'Falling back to BrowserRouter. This may indicate incorrect usage.'
        );
        return <BrowserRouter basename={basename}>{children}</BrowserRouter>;
    }

    // Create the appropriate router
    if (routerType === 'hash') {
        if (import.meta.env?.DEV) {
            console.log('[RouterChecker] Creating HashRouter with basename:', basename);
        }
        return <HashRouter basename={basename}>{children}</HashRouter>;
    }

    // Default: BrowserRouter
    if (import.meta.env?.DEV) {
        console.log('[RouterChecker] Creating BrowserRouter with basename:', basename);
    }
    return <BrowserRouter basename={basename}>{children}</BrowserRouter>;
};

export default RouterChecker;
